import React from 'react'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import { Box, Button, CircularProgress, useTheme } from '@mui/material'
import { useIntl } from 'react-intl'

import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import ConfigurationCollection from '@app/src/pages/Configurations/ConfigurationCollection'
import TemplateAvailabilityList from './TemplateAvailabilityList'
import TemplateAvailabilityDrawerView from './TemplateAvailabilityDrawerView'
import { SortOrder } from '@app/src/types/filter'
import type { QuestionnaireTemplate } from '@app/src/types/resourceExplorer'

// Component for managing questionnaire template availability configuration
const QuestionnaireTemplateAdminConfig: React.FC = () => {
  const { openDrawer } = useDrawer()
  const theme = useTheme()
  const { formatMessage } = useIntl()

  const { items: questionnaireTemplates = [], isLoading } = useFetchCollectionWithPost<QuestionnaireTemplate>({
    endpoint: endpoints.requestTemplates,
    key: FetchKey.RequestTemplate,
    payload: {
      filter: [],
      include: [
        'organizationAvailability.organization',
        'solutionAvailability',
        'assessmentQuestionnaireLink.assessmentTemplate.organizationAvailability.organization',
        'assessmentQuestionnaireLink.assessmentTemplate.solutionAvailability',
      ],
      sort: { target: 'title', order: SortOrder.ASCENDING },
    },
  })

  // Map QuestionnaireTemplate to BaseTemplate interface expected by the shared component
  const mappedTemplates = questionnaireTemplates.map(template => ({
    id: template.id,
    name: template.title, // Map title to name for consistency
    solutionAvailability: template.solutionAvailability,
    organizationAvailability: template.organizationAvailability,
    linkedTemplates: (template.assessmentQuestionnaireLink || [])
      .map(link => link.assessmentTemplate)
      .filter((at): at is NonNullable<typeof at> => at != null)
      .map(at => ({
        id: at.id,
        name: at.name,
        solutionAvailability: at.solutionAvailability,
        organizationAvailability: at.organizationAvailability,
      })),
  }))

  const handleOpenAvailabilityDrawer = (template: QuestionnaireTemplate) => {
    openDrawer(
      <TemplateAvailabilityDrawerView
        template={{
          id: template.id,
          name: template.title,
          solutionAvailability: template.solutionAvailability,
          organizationAvailability: template.organizationAvailability,
        }}
        updateEndpoint={endpoints.updateQuestionnaireTemplateAvailability(template.id)}
        invalidateQueryKey={FetchKey.RequestTemplate}
      />,
    )
  }

  return (
    <ConfigurationCollection enablePadding={{ top: false, right: true }}>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <TemplateAvailabilityList
          templates={mappedTemplates}
          icon={<ArticleOutlinedIcon fontSize="large" />}
          avatarColor={theme.palette.brandDecorative.midnightSky}
          linkedTemplatesLabel={formatMessage({ id: 'configurations.templateConfig.linkedAssessments' })}
          linkedTemplatesIcon={<FactCheckOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
          renderAction={template => {
            const originalTemplate = questionnaireTemplates.find(t => t.id === template.id)
            if (!originalTemplate) return null
            return (
              <Button
                variant="contained"
                startIcon={<SettingsOutlinedIcon />}
                onClick={() => handleOpenAvailabilityDrawer(originalTemplate)}
              >
                {formatMessage({ id: 'configurations.templateConfig.availability.configure' })}
              </Button>
            )
          }}
        />
      )}
    </ConfigurationCollection>
  )
}

export default QuestionnaireTemplateAdminConfig
